import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc.js";
import { useT } from "../../i18n/index.js";
import { ACCOUNT_OPTS } from "./finance-utils.js";

const ACCOUNTS = ACCOUNT_OPTS.map((o) => o.value);

// Editor dos saldos de abertura por conta. Confirmação simples + audit (who/when no backend).
export function OpeningBalancesModal({
  initial,
  onClose,
}: {
  initial: Record<string, number>;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const t = useT();
  const set = trpc.finance.openingBalances.set.useMutation();
  const [vals, setVals] = useState<Record<string, string>>(() =>
    Object.fromEntries(ACCOUNTS.map((a) => [a, String(initial[a] ?? 0)])),
  );
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      for (const a of ACCOUNTS) {
        const v = parseFloat(vals[a] ?? "") || 0;
        if (v !== (initial[a] ?? 0)) await set.mutateAsync({ account: a as any, amount: v });
      }
      await qc.invalidateQueries();
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 460, maxWidth: "95vw" }}
      >
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
          {t("financeOpeningBalances.title")}
        </div>
        <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 16 }}>
          {t("financeOpeningBalances.subtitle")}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {ACCOUNTS.map((a) => (
            <div key={a} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ flex: 1, fontSize: 13, color: "var(--text2)" }}>{a}</span>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  background: "var(--bg3)",
                  border: "1px solid var(--border2)",
                  borderRadius: "var(--r)",
                  padding: "0 8px",
                  height: 32,
                }}
              >
                <span style={{ fontSize: 12, color: "var(--text3)" }}>€</span>
                <input
                  type="number"
                  step={0.01}
                  value={vals[a]}
                  onChange={(e) => setVals((p) => ({ ...p, [a]: e.target.value }))}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--text)",
                    fontFamily: "var(--mono)",
                    fontSize: 13,
                    width: 130,
                    outline: "none",
                    textAlign: "right",
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        {confirming ? (
          <div
            style={{
              marginTop: 18,
              background: "rgba(245,166,35,.1)",
              border: "1px solid rgba(245,166,35,.3)",
              borderRadius: "var(--r)",
              padding: "10px 14px",
            }}
          >
            <div style={{ fontSize: 13, color: "var(--amber)", marginBottom: 10 }}>
              {t("financeOpeningBalances.confirmSave")}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => setConfirming(false)} disabled={saving}>
                {t("financeOpeningBalances.cancel")}
              </button>
              <button className="btn primary" onClick={save} disabled={saving}>
                {saving ? t("financeOpeningBalances.saving") : t("financeOpeningBalances.yesSave")}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
            <button className="btn" onClick={onClose}>
              {t("financeOpeningBalances.close")}
            </button>
            <button className="btn primary" onClick={() => setConfirming(true)}>
              {t("financeOpeningBalances.save")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
